import React, { createContext, useContext, ReactNode } from 'react';
import CryptoES from 'crypto-es';

interface Context {
  setSessionItem(key: string, item: string): void;
  getSessionItem(key: string): any;
  clearSessionItems(): void;
  setSessionItems(items: any): void;
  removeSessionItem(key: string): void;
}

let store = {};

const SECRET = process.env.REACT_APP_SESSION_KEY as string;
const STORE_NAME = process.env.REACT_APP_SESSION_NAME as string;

const syncSession = () => {
  sessionStorage.setItem(STORE_NAME, JSON.stringify(store));
};

const hash = (s) => {
  return CryptoES.SHA512(s).toString(CryptoES.enc.Base64);
};

const encrypt = (plaintext, secret) => {
  const ciphertext = CryptoES.AES.encrypt(plaintext, secret).toString();
  return btoa(ciphertext);
};

const decrypt = (ciphertext, secret) => {
  return CryptoES.AES.decrypt(atob(ciphertext), secret).toString(
    CryptoES.enc.Utf8
  );
};

const setSessionItem = (key: string, value: any): void => {
  const hashKey = hash(key);
  store[hashKey] = new Date().getTime();
  syncSession();
  sessionStorage.setItem(hashKey, encrypt(JSON.stringify(value), SECRET));
};

const setSessionItems = (items: [{ string; any }]): any => {
  Object.entries(items).forEach((i) => {
    setSessionItem(i[0], i[1]);
  });
};

const getSessionItem = (key: string): any => {
  const hashKey = hash(key);
  return store[hashKey] && sessionStorage.getItem(hashKey)
    ? JSON.parse(decrypt(sessionStorage.getItem(hashKey), SECRET))
    : undefined;
};

const removeSessionItem = (key: string): any => {
  const hashKey = hash(key);
  if (store[hashKey]) {
    sessionStorage.removeItem(store[hashKey]);
  }
  delete store[hashKey];
  syncSession();
};

const clearSessionItems = (): void => {
  Object.keys(store).forEach((k) => {
    if (store[k]) {
      sessionStorage.removeItem(k);
    }
    delete store[k];
  });
  syncSession();
};

const value = {
  getSessionItem,
  setSessionItems,
  setSessionItem,
  clearSessionItems,
  removeSessionItem,
};

const SessionContext = createContext<Context>(value);

export const useSession = () => {
  const context = useContext(SessionContext);

  if (context === undefined) {
    throw new Error('useSession must be used within a SessionProvider');
  }

  if (sessionStorage.getItem(STORE_NAME)) {
    store = JSON.parse(sessionStorage.getItem(STORE_NAME) as string);
  } else {
    syncSession();
  }

  return context;
};

export const SessionProvider: React.FC<{ children: ReactNode }> = ({
  children,
}) => {
  return (
    <SessionContext.Provider value={value}>{children}</SessionContext.Provider>
  );
};
